import type { U3141PlanUploadProgressWsMessage } from '../../u2000Core/types/U2000WebSocketMessages';

export type U3141PlanUploadProgressStatus = 'idle' | 'running' | 'success' | 'partial' | 'failed';

export interface U3141PlanUploadFileError {
  entryPath: string;
  message: string;
}

export interface U3141PlanUploadProgressUiState {
  open: boolean;
  percent: number;
  current: number;
  total: number;
  zipArchiveCount: number;
  fileLabel: string | null;
  status: U3141PlanUploadProgressStatus;
  globalError: string | null;
  fileErrors: U3141PlanUploadFileError[];
  doneSuccessCount: number | null;
  doneErrorCount: number | null;
}

export interface U3141PlanUploadProgressWatchContext {
  clientRequestUuid: string | null;
  masterLotChainId: string | undefined;
}

export function u3141InitialPlanUploadProgressState(): U3141PlanUploadProgressUiState {
  return {
    open: false,
    percent: 0,
    current: 0,
    total: 0,
    zipArchiveCount: 0,
    fileLabel: null,
    status: 'idle',
    globalError: null,
    fileErrors: [],
    doneSuccessCount: null,
    doneErrorCount: null,
  };
}

export function u3141PlanUploadEntryDisplayName(entryPath: string): string {
  const normalized = entryPath.replace(/\\/g, '/');
  const segments = normalized.split('/').filter(Boolean);
  return segments.length > 0 ? segments[segments.length - 1]! : entryPath;
}

export function u3141PlanUploadProgressPercent(current: number, total: number): number {
  if (total <= 0) {
    return 0;
  }
  return Math.max(0, Math.min(100, Math.round((current / total) * 100)));
}

export function u3141ShouldHandlePlanUploadProgress(
  payload: U3141PlanUploadProgressWsMessage,
  ctx: U3141PlanUploadProgressWatchContext
): boolean {
  if (!ctx.clientRequestUuid || !ctx.masterLotChainId) {
    return false;
  }
  return (
    payload.clientRequestUuid === ctx.clientRequestUuid &&
    payload.masterLotChainId === ctx.masterLotChainId
  );
}

export function u3141ApplyPlanUploadProgressEvent(
  prev: U3141PlanUploadProgressUiState,
  payload: U3141PlanUploadProgressWsMessage
): U3141PlanUploadProgressUiState {
  switch (payload.phase) {
    case 'started':
      return {
        ...prev,
        open: true,
        status: 'running',
        total: payload.total,
        zipArchiveCount: payload.zipArchiveCount,
        current: 0,
        percent: 0,
        fileLabel: null,
        globalError: null,
        fileErrors: [],
        doneSuccessCount: null,
        doneErrorCount: null,
      };
    case 'file': {
      const fileErrors = payload.error
        ? [...prev.fileErrors, { entryPath: payload.entryPath, message: payload.error }]
        : prev.fileErrors;
      return {
        ...prev,
        open: true,
        status: 'running',
        current: payload.current,
        total: payload.total,
        fileLabel: u3141PlanUploadEntryDisplayName(payload.entryPath),
        percent: u3141PlanUploadProgressPercent(payload.current, payload.total),
        fileErrors,
      };
    }
    case 'done': {
      let status: U3141PlanUploadProgressStatus = 'failed';
      if (payload.anySuccess && payload.errorCount === 0) {
        status = 'success';
      } else if (payload.anySuccess) {
        status = 'partial';
      }
      return {
        ...prev,
        open: true,
        status,
        current: prev.total > 0 ? prev.total : prev.current,
        percent: prev.total > 0 ? 100 : prev.percent,
        doneSuccessCount: payload.successCount,
        doneErrorCount: payload.errorCount,
        globalError: null,
      };
    }
    case 'failed':
      return {
        ...prev,
        open: true,
        status: 'failed',
        globalError: payload.message,
        percent: prev.percent,
      };
    default:
      return prev;
  }
}
